import { QueryKey, useMutation, useQueryClient } from '@tanstack/react-query'

import { successToast } from 'common/components/toastNotification'

import { unpinContent } from '../api'
import { ContentLibraryEntry } from '../queries/useGetContentLibraryQuery'
import { contentKeys } from '../queryKeys'

type ContentLibraryQueryData = {
  data: ContentLibraryEntry[]
  total: number
  pinnedContents: ContentLibraryEntry[]
}

type UnpinMutationContext = {
  previousQueries: [QueryKey, ContentLibraryQueryData | undefined][]
}

const useUnpinContentMutation = () => {
  const queryClient = useQueryClient()

  return useMutation(
    ({ accountId, contentId }: { accountId: string; contentId: string }) =>
      unpinContent({ accountId, contentId }),
    {
      onMutate: async ({ contentId }): Promise<UnpinMutationContext> => {
        await queryClient.cancelQueries(contentKeys.contentLibraryAll())
        const previousQueries =
          queryClient.getQueriesData<ContentLibraryQueryData>(
            contentKeys.contentLibraryAll()
          )

        previousQueries.forEach(([queryKey]) => {
          queryClient.setQueryData<ContentLibraryQueryData>(queryKey, (old) => {
            if (!old) {
              return old
            }

            const data = old.data.map((entry) =>
              entry.id === contentId ? { ...entry, isPinned: false } : entry
            )
            const pinnedContents = old.pinnedContents.filter(
              (entry) => entry.id !== contentId
            )

            return {
              ...old,
              data,
              pinnedContents,
            }
          })
        })

        return { previousQueries }
      },
      onError: (_error, _variables, context) => {
        context?.previousQueries.forEach(([queryKey, queryData]) => {
          queryClient.setQueryData(queryKey, queryData)
        })
      },
      onSuccess: () => {
        successToast('Campaign unpinned successfully.')
      },
      onSettled: () => {
        queryClient.invalidateQueries(contentKeys.contentLibraryAll())
      },
    }
  )
}

export default useUnpinContentMutation
